import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import { PLACE_ID_PATTERN } from '@bandido/shared/types';

import { CONFIG } from '@frontend/config';
import {
  CatalogEntry,
  FerrySearchPayload,
  useFerryRouteConnectivity,
  usePlaceCoords,
  useSearchFerry,
} from '../queries';
import {
  cleanParams,
  enumParam,
  isIncompleteSearch,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import {
  FerryOffer,
  FerrySort,
  ferryPlaceFromParams,
  ferryRouteMapPoints,
  ferrySearchMatchesPair,
  isRoundTrip,
  sortFerryOffers,
} from '../ferry';
import { formatLocalDate, formatLocalTime } from '../rail';
import { PlaceRef } from '../places';
import { committedPlaceCoordsIndex, committedPlaceId } from '../placeCoords';
import { FerryVehicleInput } from './FerryCatalogInputs';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { describeFerry } from '../describeSearch';
import { SearchBar, SearchDivider, SearchField } from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_HALF_SHARE,
  SEARCH_PASSENGER_FIELD_WIDTH,
} from '../searchFieldLayout';
import { RecentSearches } from './RecentSearches';
import { FerryOfferCard } from './FerryOfferCard';
import { BookingModal } from './BookingModal';
import {
  committedParty,
  ferryBookingPassengerCount,
} from '../committedBookingContext';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import { useSearchResultAuthority } from '../useSearchResultAuthority';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import { searchRouteMapKey } from '../routeMap';
import { SearchRouteMap } from './SearchRouteMap';
import {
  FERRY_PASSENGER_RULES,
  ferryDraftPassengerParam,
  ferryPassengersFromParams,
  ferryPayloadPassengersFromDrafts,
  ferryPayloadPassengersFromParams,
} from '../searchPassengers';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import { useBookingCaseContext } from './BookingCaseContext';
import {
  latestSearchDate,
  reconcileSearchEndDate,
  todaySearchDate,
} from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';

/** Monedas en las que el proveedor cotiza la travesía. */
const CURRENCIES = ['EUR', 'GBP', 'USD'];

/**
 * Buscador de ferrys. Origen y destino se
 * eligen contra el maestro de lugares (`PlaceAutocomplete`, `servedBy` de los
 * conectores ferry, sin `kinds` — Dover es ciudad, no puerto) y la búsqueda
 * viaja con el `plc_*`; el destino queda libre, sin resolver códigos de puerto
 * ni chequear conectividad en Bandido. En ambos casos, vuelta
 * OPCIONAL — su presencia convierte la búsqueda en roundtrip y el precio pasa
 * a ser el total ida+vuelta — y vehículo opcional del maestro de modelos (la
 * tarifa depende de sus dimensiones); el vehículo NO migra a places en este
 * issue.
 */
function payloadFromParams(
  params: Record<string, string>,
): FerrySearchPayload | null {
  if (
    params.buscar !== '1' ||
    !params.origen ||
    !params.destino ||
    !params.fecha ||
    !PLACE_ID_PATTERN.test(params.origen) ||
    !PLACE_ID_PATTERN.test(params.destino)
  ) {
    return null;
  }
  const passengerPayload = ferryPayloadPassengersFromParams(params);
  if (!passengerPayload) return null;
  return {
    fromPort: params.origen,
    toPort: params.destino,
    date: params.fecha,
    ...(params.fechaVuelta ? { returnDate: params.fechaVuelta } : {}),
    ...passengerPayload,
    ...(params.vehiculo ? { vehicleRef: params.vehiculo } : {}),
    currency: params.moneda ?? 'EUR',
  };
}

/**
 * Vehículo desde la URL: id + nombre alcanzan para el autocomplete. Este es el
 * único catálogo nativo del formulario; origen y destino son exclusivamente
 * referencias canónicas `plc_*` y se reconstruyen con helpers de Places.
 */
function entryFromParams(id?: string, name?: string): CatalogEntry | null {
  return id ? { id, name: name ?? id } : null;
}

export function FerrySearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'ferry', dateParam: 'fecha' });
  const searchPayload = useMemo(
    () => payloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchFerry(searchPayload);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload
    ? quoteSearchIdentity('ferry', CONFIG.AGENCY_ID, searchPayload)
    : undefined;

  const [fromPlace, setFromPlace] = useState<PlaceRef | null>(() =>
    ferryPlaceFromParams(url.params.origen, url.params.origenNombre));
  const [toPlace, setToPlace] = useState<PlaceRef | null>(() =>
    ferryPlaceFromParams(url.params.destino, url.params.destinoNombre));
  const fromName = fromPlace?.name;
  const toName = toPlace?.name;
  const fromId = fromPlace?.id;
  const toId = toPlace?.id;
  const [date, setDate] = useState(() => url.params.fecha ?? '');
  const [returnDate, setReturnDate] = useState(() => url.params.fechaVuelta ?? '');
  const [returnDateOpen, setReturnDateOpen] = useState(false);
  const [withReturn, setWithReturn] = useState(() => Boolean(url.params.fechaVuelta));
  const [passengers, setPassengers] = useState(
    () => ferryPassengersFromParams(url.params) ?? [],
  );
  const [passengersEdited, setPassengersEdited] = useState(false);
  const { partySuggestion, consumePartySuggestion } = useBookingCaseContext();
  useEffect(() => {
    if (!partySuggestion) return;
    setPassengers(
      ferryPassengersFromParams({ pax: String(partySuggestion.size) }) ?? [],
    );
    setPassengersEdited(true);
    consumePartySuggestion(partySuggestion.revision);
  }, [partySuggestion, consumePartySuggestion]);
  const [withVehicle, setWithVehicle] = useState(() => Boolean(url.params.vehiculo));
  const [vehicle, setVehicle] = useState<CatalogEntry | null>(() =>
    entryFromParams(url.params.vehiculo, url.params.vehiculoNombre));
  const [currency, setCurrency] = useState(() => url.params.moneda ?? 'EUR');
  const [sort, setSort] = useState<FerrySort>(() =>
    enumParam(url.params, 'orden', ['departure', 'price', 'duration'] as const, 'departure'));
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<FerryOffer> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;
  /** El vehículo con el que se cotizó lo que está en pantalla sale de la URL. */
  const searchedVehicle = entryFromParams(
    searchPayload?.vehicleRef,
    url.params.vehiculoNombre,
  );

  // Al desactivar los toggles, el dato asociado se descarta.
  useEffect(() => {
    if (!withReturn) setReturnDate('');
  }, [withReturn]);
  useEffect(() => {
    if (!withVehicle) setVehicle(null);
  }, [withVehicle]);

  const committedPassengers = ferryBookingPassengerCount(searchPayload);
  const passengerPayload = ferryPayloadPassengersFromDrafts(passengers);
  const minDate = todaySearchDate();
  const minReturnDate = latestSearchDate(minDate, date);

  const canSearch = Boolean(
    fromId &&
      toId &&
      date &&
      date >= minDate &&
      passengerPayload &&
      (!withReturn || (returnDate && returnDate >= minReturnDate)) &&
      (!withVehicle || vehicle),
  );

  /**
   * El formulario tal como se escribiría en la URL. Es la ÚNICA derivación del
   * borrador: de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    // Se manda el CÓDIGO del catálogo (inequívoco: hay "Dover" en más de un
    // país) o el `plc_*` en modo places; el nombre solo sirve para repintar
    // el autocomplete.
    origen: fromId,
    origenNombre: fromName,
    destino: toId,
    destinoNombre: toName,
    fecha: date,
    fechaVuelta: withReturn && returnDate ? returnDate : undefined,
    pax: ferryDraftPassengerParam(url.params, passengers, passengersEdited),
    // Solo se limpian al confirmar el formato nuevo; el adaptador todavía los
    // interpreta para que las búsquedas guardadas anteriores sigan abriendo.
    adultos: undefined,
    menores: undefined,
    edadesAdultos: undefined,
    edades: undefined,
    vehiculo: withVehicle && vehicle ? vehicle.id : undefined,
    vehiculoNombre: withVehicle && vehicle ? vehicle.name : undefined,
    moneda: currency,
  };
  const draftPayload = payloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);

  const handleBook = (offer: FerryOffer) => {
    setBookingQuote(
      captureSelectableSearchQuote(
        offer,
        search.dataUpdatedAt,
        'ferry',
        CONFIG.AGENCY_ID,
        searchPayload,
        Date.now(),
        formDiverged,
      ),
    );
  };

  const committedFromId = committedPlaceId(searchPayload?.fromPort);
  const committedToId = committedPlaceId(searchPayload?.toPort);
  const committedFromCoords = usePlaceCoords(committedFromId);
  const committedToCoords = usePlaceCoords(committedToId);
  const bookingPlaceCoords = useMemo(
    () => committedPlaceCoordsIndex([
      { id: committedFromId, selection: fromPlace, resolved: committedFromCoords.data },
      { id: committedToId, selection: toPlace, resolved: committedToCoords.data },
    ]),
    [committedFromId, committedToId, fromPlace, toPlace, committedFromCoords.data, committedToCoords.data],
  );
  const bookingRoutePoints = ferryRouteMapPoints(searchPayload, bookingPlaceCoords, {
    from: url.params.origenNombre,
    to: url.params.destinoNombre,
  });

  /**
   * En PLACES, con AMBOS lugares elegidos: un chequeo barato (catálogo
   * cacheado `ferry/routes?from=&to=`, no una búsqueda priceada) que avisa
   * ANTES de buscar si no hay ninguna ruta entre los dos — sin esto, la
   * única señal sería la búsqueda ya priceada volviendo vacía, y con el
   * destino libre (sin el filtrado que tiene el nativo) "no hay ruta" pasa
   * a ser un caso común, no la excepción. NO bloquea la búsqueda (el
   * catálogo puede tener algún desvío con el vivo): es un aviso, no un gate.
   *
   * Se retira apenas hay una búsqueda YA CORRIDA para este MISMO par
   * (`searchMatchesCurrentPair`): el resultado autoritativo manda (21.23) —
   * un catálogo desalineado con el proveedor en vivo no puede seguir
   * diciendo "sin ruta" arriba de una lista real de travesías. Comparado
   * contra el par ACTUAL de los pickers, no solo "hay alguna búsqueda":
   * si el asesor cambia el destino después de buscar, la búsqueda vieja no
   * habla del par nuevo y el aviso fresco tiene que seguir viéndose.
   */
  const { data: routeConnected, isFetching: checkingConnectivity } =
    useFerryRouteConnectivity(
      fromId,
      toId,
    );
  const searchMatchesCurrentPair =
    Boolean(search.data) && ferrySearchMatchesPair(searchPayload, fromId, toId);
  const noKnownRoute =
    Boolean(fromId && toId) &&
    routeConnected === false &&
    !checkingConnectivity &&
    !searchMatchesCurrentPair;

  /** Vacía la búsqueda: campos, URL y memoria de navegación. */
  const handleClear = () => {
    setFromPlace(null);
    setToPlace(null);
    setDate('');
    setReturnDate('');
    setWithReturn(false);
    setPassengers(ferryPassengersFromParams({}) ?? []);
    setPassengersEdited(false);
    setWithVehicle(false);
    setVehicle(null);
    url.reset();
  };

  const handleSearch = () => {
    if (!canSearch || !fromId || !toId || !fromName || !toName) return;
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('ferry', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  const results = (search.data?.results ?? []) as FerryOffer[];
  const sorted = useMemo(() => sortFerryOffers(results, sort), [results, sort]);

  const bookingDetails = bookingOffer
    ? [
      `${formatLocalDate(bookingOffer.departure)} · ${formatLocalTime(
        bookingOffer.departure,
      )} ${bookingOffer.fromPort ?? ''} → ${formatLocalTime(bookingOffer.arrival)} ${
        bookingOffer.toPort ?? ''
      }`,
      ...(isRoundTrip(bookingOffer)
        ? [
          `${t('travelServices.booking.return')}: ${formatLocalDate(
            bookingOffer.returnDeparture,
          )} · ${formatLocalTime(bookingOffer.returnDeparture)} ${
            bookingOffer.toPort ?? ''
          } → ${formatLocalTime(bookingOffer.returnArrival)} ${bookingOffer.fromPort ?? ''}`,
        ]
        : []),
      [
        bookingOffer.operator ?? '',
        `${t('travelServices.pax')}: ${committedPassengers}`,
        searchedVehicle ? searchedVehicle.name : t('travelServices.ferry.onFoot'),
      ]
        .filter(Boolean)
        .join(' · '),
    ]
    : undefined;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Modo del viaje: cambian QUÉ se busca, así que van sobre la barra —
          como las pestañas ida/vuelta de Direct Ferries — y no mezclados
          entre los campos. */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={withReturn}
              onChange={(event) => setWithReturn(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">{t('travelServices.ferry.addReturn')}</Typography>
          }
        />
        <Tooltip title={t('travelServices.ferry.vehicleTooltip')}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={withVehicle}
                onChange={(event) => setWithVehicle(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {t('travelServices.ferry.withVehicle')}
              </Typography>
            }
          />
        </Tooltip>
      </Box>

      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <SearchField label={t('travelServices.ferry.fromPort')} grow width={200}>
          <PlaceAutocomplete
            label={t('travelServices.ferry.fromPort')}
            vertical="ferry"
            value={fromPlace}
            onChange={setFromPlace}
            width="100%"
            required
          />
        </SearchField>
        <Tooltip title={t('travelServices.transfers.swap')}>
          <IconButton
            size="small"
            sx={{ alignSelf: 'center', mx: 0.5 }}
            onClick={() => {
              setFromPlace(toPlace);
              setToPlace(fromPlace);
            }}
          >
            <SwapHoriz />
          </IconButton>
        </Tooltip>
        <SearchField label={t('travelServices.ferry.toPort')} grow width={200}>
          <PlaceAutocomplete
            label={t('travelServices.ferry.toPort')}
            vertical="ferry"
            value={toPlace}
            onChange={setToPlace}
            width="100%"
            required
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.ferry.date')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={withReturn ? SEARCH_FIELD_HALF_SHARE : undefined}
        >
          <SearchDatePicker
            label={t('travelServices.ferry.date')}
            value={date}
            minDate={minDate}
            onChange={(nextDate) => {
              setDate(nextDate);
              setReturnDate((current) => reconcileSearchEndDate(nextDate, current));
            }}
            onAccept={() => {
              if (withReturn) setReturnDateOpen(true);
            }}
            required
          />
        </SearchField>
        {withReturn && <SearchDivider />}
        {withReturn && (
          <SearchField
            label={t('travelServices.ferry.returnDate')}
            width={SEARCH_DATE_FIELD_WIDTH}
            xsShare={SEARCH_FIELD_HALF_SHARE}
          >
            <SearchDatePicker
              label={t('travelServices.ferry.returnDate')}
              value={returnDate}
              onChange={setReturnDate}
              minDate={minReturnDate}
              open={returnDateOpen}
              onOpen={() => setReturnDateOpen(true)}
              onClose={() => setReturnDateOpen(false)}
              required
            />
          </SearchField>
        )}
        <SearchDivider />
        <SearchField
          label={t('travelServices.passengerPicker.label')}
          width={SEARCH_PASSENGER_FIELD_WIDTH}
        >
          <SearchPassengerPicker
            value={passengers}
            onChange={(nextPassengers) => {
              setPassengersEdited(true);
              setPassengers(nextPassengers);
            }}
            rules={FERRY_PASSENGER_RULES}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.ferry.currency')}
          width={100}
        >
          <TextField
            select
            size="small"
            label={t('travelServices.ferry.currency')}
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          >
            {CURRENCIES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
        </SearchField>
      </SearchBar>

      {/* Detalles que aparecen según lo elegido: apretarlos dentro de la barra
          la desarma justo cuando más campos tiene. */}
      {withVehicle && (
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          {withVehicle && (
            <FerryVehicleInput
              label={t('travelServices.ferry.vehicle')}
              value={vehicle}
              onChange={setVehicle}
            />
          )}

        </Box>
      )}

      {/* Las búsquedas recientes NO dependen de que haya detalles que mostrar:
          son la forma de retomar una búsqueda anterior en el formulario vacío,
          que es justo cuando ninguno de los toggles está puesto. */}
      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="ferry"
          describe={(params) => describeFerry(params, t, i18n.language)}
        />
      )}

      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      {/* Solo PLACES: el destino se elige libre (ver useFerryRouteConnectivity
          más arriba) — este aviso ADELANTA lo que la búsqueda vacía diría
          después, antes de que el asesor complete fechas/pasajeros. No
          bloquea: el catálogo puede tener algún desvío con el proveedor en
          vivo. */}
      {noKnownRoute && (
        <Alert severity="warning">
          {t('travelServices.ferry.noKnownRoute', {
            fromPort: fromName ?? '',
            toPort: toName ?? '',
          })}
        </Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeFerry(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {/* El recorrido de la búsqueda comprometida, uno por búsqueda y
          colapsado: el iframe no existe hasta que el asesor lo abre. La
          identidad de la búsqueda va de `key` para que una búsqueda nueva
          arranque cerrada y sin el mapa de la anterior. */}
      {url.params.buscar === '1' && searchPayload && (
        <SearchRouteMap
          key={searchRouteMapKey(activeSearchIdentity, bookingRoutePoints)}
          vertical="ferry"
          points={bookingRoutePoints}
        />
      )}

      {search.isLoading && <ResultsSkeleton archetype="schedule" />}
      {search.isError && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      <ProviderStatusStrip providers={search.data?.providers} />
      {!search.data && !search.isLoading && !search.isError && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.ferry.emptyState')}
        </Typography>
      )}

      {search.data && !search.isLoading && (
        <>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {t('travelServices.ferry.found', { count: sorted.length })}
            </Typography>
            {sorted.length > 1 && (
              <TextField
                select
                size="small"
                sx={{ width: 170 }}
                label={t('travelServices.cars.sort')}
                value={sort}
                onChange={(event) => setSort(event.target.value as FerrySort)}
              >
                <MenuItem value="departure">
                  {t('travelServices.ferry.sortDeparture')}
                </MenuItem>
                <MenuItem value="price">{t('travelServices.cars.sortPrice')}</MenuItem>
                <MenuItem value="duration">
                  {t('travelServices.ferry.sortDuration')}
                </MenuItem>
              </TextField>
            )}
          </Box>
          {sorted.length === 0 ? (
            <Alert severity="info">
              {t('travelServices.ferry.noRoute', {
                fromPort: fromName ?? '',
                toPort: toName ?? '',
              })}
            </Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {sorted.map((offer, index) => (
                <FerryOfferCard
                  key={`${offer.provider}-${offer.departure}-${index}`}
                  offer={offer}
                  index={index}
                  vehicleName={searchedVehicle?.name}
                  bookingDisabled={!resultAuthority.canSelectResult}
                  onBook={handleBook}
                />
              ))}
            </Box>
          )}
        </>
      )}

      <BookingModal
        vertical="ferry"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={bookingDetails}
        routePoints={bookingRoutePoints}
        party={committedParty({ vertical: 'ferry', payload: searchPayload })}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}
